import React, { lazy, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import { COLORS, FONTS } from "./styles/variables";
import AddToHomeScreen from "./components/AddToHomeScreen/AddToHomeScreen";
import GoogleAnalytics from "./components/Analytics/GoogleAnalytics";
import GoogleTagManager from "./components/Analytics/GoogleTagManager";
import GTMNoScript from "./components/Analytics/GTMNoScript";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import FloatingButton from "./components/FloatingButton/FloatingButton";
import ImagePreloader from "./components/Performance/ImagePreloader";
import LazyPageWrapper from "./components/Performance/LazyPageWrapper";
import PerformanceDashboard from "./components/Performance/PerformanceDashboard";
import initCoreWebVitals from "./utils/coreWebVitals";

// Páginas críticas - carga inmediata
import HomePage from "./pages/HomePage";
import ComoFuncionaPage from "./pages/ComoFuncionaPage";

// Páginas con lazy loading - carga bajo demanda
const AboutPage = lazy(() => import("./pages/about/AboutPage"));
const AppPage = lazy(() => import("./pages/app/AppPage"));
const BusinessPilotPage = lazy(() => import("./pages/business/BusinessPilotPage"));
const B2BPresentationPage = lazy(() =>
  import("./pages/b2b/B2BPresentationPage")
);
const FaqPage = lazy(() => import("./pages/faq/FaqPage"));
const TransportistaPage = lazy(() =>
  import("./pages/transportista/TransportistaPage")
);
const MarketingPage = lazy(() => import("./pages/marketing/MarketingPage"));
const ContactPage = lazy(() => import("./pages/contact/ContactPage"));

// Páginas locales con lazy loading
const SantiagoPage = lazy(() => import("./pages/local/SantiagoPage"));
const ValparaisoPage = lazy(() => import("./pages/local/ValparaisoPage"));
const ConcepcionPage = lazy(() => import("./pages/local/ConcepcionPage"));

// Páginas de autenticación
const AuthCallbackPage = lazy(() => import("./pages/auth/AuthCallbackPage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));

// Páginas adicionales con lazy loading
const CardSuccessPage = lazy(() =>
  import("./pages/card-success/CardSuccessPage")
);
const CookiesPage = lazy(() => import("./pages/cookies/CookiesPage"));
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const DeleteAccountPage = lazy(() =>
  import("./pages/delete-account/DeleteAccountPage")
);
const PrivacyPage = lazy(() => import("./pages/privacy/PrivacyPage"));
const SupportPage = lazy(() => import("./pages/support/SupportPage"));
const TermsPage = lazy(() => import("./pages/terms/TermsPage"));

const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    color: ${COLORS.BLACK_TEXT};
    background-color: ${COLORS.WHITE};
    font-size: ${FONTS.SIZE_BODY};
    line-height: 1.6;
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  /* Optimizaciones para dispositivos táctiles */
  @media (hover: none) and (pointer: coarse) {
    /* Aumentar tamaño de elementos interactivos en móviles */
    button, 
    a, 
    input[type="button"], 
    input[type="submit"],
    .clickable {
      min-height: 44px;
      min-width: 44px;
    }
    
    /* Mejorar espaciado para facilitar el toque */
    nav a, 
    .menu-item,
    .touch-target {
      padding: 12px;
      margin: 4px;
    }
  }
`;

const AppChrome = () => {
  const { pathname } = useLocation();
  const isPresentation = pathname === "/b2b";

  return (
    <>
      {!isPresentation && (
        <>
          <FloatingButton />
          <AddToHomeScreen />
        </>
      )}
      <GoogleAnalytics />
      <GoogleTagManager />
      <GTMNoScript />
      <ImagePreloader />
      <PerformanceDashboard
        isVisible={!isPresentation && process.env.NODE_ENV === "development"}
      />
    </>
  );
};

const App = () => {
  // Inicializar tracking de Core Web Vitals
  useEffect(() => {
    initCoreWebVitals();
  }, []);

  return (
    <HelmetProvider>
      <GlobalStyle />
      <Router>
        <Routes>
          {/* Páginas críticas - carga inmediata */}
          <Route path="/" element={<HomePage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />

          {/* URLs semánticas con lazy loading */}
          <Route
            path="/fletes-santiago"
            element={
              <LazyPageWrapper loadingMessage="Cargando información de Santiago...">
                <SantiagoPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/fletes-valparaiso"
            element={
              <LazyPageWrapper loadingMessage="Cargando información de Valparaíso...">
                <ValparaisoPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/fletes-concepcion"
            element={
              <LazyPageWrapper loadingMessage="Cargando información de Concepción...">
                <ConcepcionPage />
              </LazyPageWrapper>
            }
          />

          {/* Páginas principales con lazy loading */}
          <Route
            path="/app"
            element={
              <LazyPageWrapper loadingMessage="Cargando página de descarga...">
                <AppPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/empresas"
            element={
              <LazyPageWrapper loadingMessage="Cargando pilotos B2B...">
                <BusinessPilotPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/b2b"
            element={
              <LazyPageWrapper loadingMessage="Cargando presentación B2B...">
                <B2BPresentationPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/preguntas-frecuentes"
            element={
              <LazyPageWrapper loadingMessage="Cargando preguntas frecuentes...">
                <FaqPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/ser-transportista"
            element={
              <LazyPageWrapper loadingMessage="Cargando información para transportistas...">
                <TransportistaPage />
              </LazyPageWrapper>
            }
          />

          {/* Rutas originales mantenidas para compatibilidad */}
          <Route
            path="/nosotros"
            element={
              <LazyPageWrapper>
                <AboutPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/faq"
            element={
              <LazyPageWrapper>
                <FaqPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/transportista"
            element={
              <LazyPageWrapper>
                <TransportistaPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/contacto"
            element={
              <LazyPageWrapper>
                <ContactPage />
              </LazyPageWrapper>
            }
          />

          {/* Páginas adicionales con lazy loading */}
          <Route
            path="/terminos"
            element={
              <LazyPageWrapper>
                <TermsPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/privacidad"
            element={
              <LazyPageWrapper>
                <PrivacyPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/cookies"
            element={
              <LazyPageWrapper>
                <CookiesPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/soporte"
            element={
              <LazyPageWrapper>
                <SupportPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/marketing"
            element={
              <LazyPageWrapper>
                <MarketingPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/eliminar-cuenta"
            element={
              <LazyPageWrapper>
                <DeleteAccountPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/tarjeta-registrada"
            element={
              <LazyPageWrapper>
                <CardSuccessPage />
              </LazyPageWrapper>
            }
          />

          {/* Rutas de autenticación con lazy loading */}
          <Route
            path="/login"
            element={
              <LazyPageWrapper>
                <LoginPage />
              </LazyPageWrapper>
            }
          />
          <Route
            path="/auth/callback"
            element={
              <LazyPageWrapper>
                <AuthCallbackPage />
              </LazyPageWrapper>
            }
          />

          {/* Rutas protegidas */}
          <Route
            path="/register"
            element={
              <ProtectedRoute>
                <LazyPageWrapper>
                  <RegisterPage />
                </LazyPageWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <LazyPageWrapper>
                  <DashboardPage />
                </LazyPageWrapper>
              </ProtectedRoute>
            }
          />
        </Routes>
        <AppChrome />
      </Router>
    </HelmetProvider>
  );
};

export default App;
